import { useTranslation } from 'react-i18next';
import { buildMetricView, type TargetedMetric } from '../../services/overviewMetricsService';
import { formatDkkCompact } from '../../utils/currencyUtils';
import { TargetTrack } from '../UI';
import { MoneyLegend } from './MoneyLegend';
import styles from './MoneyMetric.module.css';

interface MoneyMetricProps {
  // Narrowed by the caller: this component is only rendered for a location that
  // has a ceiling, and the type says so rather than trusting that.
  metric: TargetedMetric;
  completionDependentMoney: number;
  lockedFromWeek: number;
  lockedToWeek: number;
}

/**
 * The location's money against its target: the metric that drives the KPIs.
 *
 * Only rendered where a target exists — without a ceiling the track has no scale
 * and no meaning, so the card shows figures instead.
 */
export const MoneyMetric = ({
  metric,
  completionDependentMoney,
  lockedFromWeek,
  lockedToWeek
}: MoneyMetricProps) => {
  const { t, i18n } = useTranslation();
  const view = buildMetricView(metric);
  const format = (value: number): string => formatDkkCompact(value, i18n.language);

  return (
    <section className={styles.metric}>
      <div className={styles.header}>
        <span className={styles.label}>
          {t('overview-money')} <span className={styles.qualifier}>{t('overview-potential')}</span>
        </span>
        <span className={styles.figure}>
          {format(view.potential)}
          <span className={styles.against}> / {format(view.target)}</span>
        </span>
      </div>
      <TargetTrack
        lockedPercent={view.lockedPercent}
        openPercent={view.openPercent}
        excessPercent={view.excessPercent}
        targetLabel={t('overview-target-flag')}
        realised={
          view.realised > 0
            ? {
                percent: view.realisedPercent,
                label: t('overview-realised-value', { value: format(view.realised) })
              }
            : undefined
        }
      />
      <MoneyLegend view={view} lockedFromWeek={lockedFromWeek} lockedToWeek={lockedToWeek} />
      {completionDependentMoney > 0 && (
        <p className={styles.caveat}>
          {t('overview-caveat-completion', { value: format(completionDependentMoney) })}
        </p>
      )}
    </section>
  );
};
